/**
 * 目录树工具：将扁平目录列表转换为 TreeSelect 所需的树形结构
 * @author dongdong.fdd
 * @date 2025/3/1
 */

export interface DirTreeNode {
  value: string;
  title: string;
  children?: DirTreeNode[];
}

const SYSTEM_DIRS = ['全部目录', '默认目录', '__all__', '__default__'];

/**
 * 将扁平目录列表转换为树形结构
 * @param flatDirs 扁平目录列表，如 ["全部目录", "默认目录", "images", "images/2024", "images/2024/q1"]
 * @returns TreeSelect 所需的 treeData
 */
export function buildDirTree(flatDirs: string[]): DirTreeNode[] {
  if (!flatDirs || flatDirs.length === 0) {
    return [];
  }

  const systemNodes: DirTreeNode[] = [];
  const userPaths: string[] = [];

  for (const d of flatDirs) {
    if (SYSTEM_DIRS.includes(d)) {
      systemNodes.push({ value: d, title: d });
    } else {
      userPaths.push(d);
    }
  }

  const treeMap = new Map<string, DirTreeNode>();

  for (const path of userPaths) {
    const parts = path.split('/').filter(Boolean);
    for (let i = 0; i < parts.length; i++) {
      const fullPath = parts.slice(0, i + 1).join('/');
      if (!treeMap.has(fullPath)) {
        treeMap.set(fullPath, {
          value: fullPath,
          title: parts[i],
          children: [],
        });
      }
    }
  }

  const childIds = new Set<string>();
  const allPaths = Array.from(treeMap.keys()).sort((a, b) => a.split('/').length - b.split('/').length);

  for (const path of allPaths) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length <= 1) continue;

    const node = treeMap.get(path);
    const parentPath = parts.slice(0, -1).join('/');
    const parent = treeMap.get(parentPath);
    if (!node || !parent?.children) continue;

    if (!parent.children.some((c) => c.value === path)) {
      parent.children.push(node);
    }
    childIds.add(path);
  }

  const rootNodes = Array.from(treeMap.values()).filter((n) => !childIds.has(n.value));
  return [...systemNodes, ...rootNodes];
}

/**
 * 递归替换树节点的 title
 */
export function mapDirTreeTitles(
  nodes: DirTreeNode[],
  formatLabel: (value: string) => string
): DirTreeNode[] {
  return nodes.map((node) => ({
    ...node,
    title: formatLabel(node.value),
    children: node.children?.length
      ? mapDirTreeTitles(node.children, formatLabel)
      : undefined,
  }));
}
